import { SampleCategories, SampleVariant } from 'api';
import { TextField } from 'form';
import { useTranslation } from 'i18n';
import { InputAdornment, Stack } from 'ui';

import { DefaultBreakdownFormFields } from '../DefaultBreakdownFormFields/DefaultBreakdownFormFields';
import { ManualBreakdownFormFields } from '../ManualBreakdownFormFields/ManualBreakdownFormFields';

const BreakdownFormFields = ({ variant }: { variant?: SampleVariant }) => {
  const { t } = useTranslation('features');

  switch (variant) {
    case 'alu':
      return (
        <ManualBreakdownFormFields
          fields={[
            {
              group: t('evidence.sampleFormFields.breakdowns.alu.groups.aluminium'),
              items: [
                {
                  label: t('evidence.sampleFormFields.breakdowns.alu.items.ubc'),
                  name: SampleCategories.aluminium_ubc,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.alu.items.foilAndTray'),
                  name: SampleCategories.aluminium_foil_and_tray,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.alu.items.foodCan'),
                  name: SampleCategories.aluminium_food_can,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.alu.items.otherAluminium'),
                  name: SampleCategories.aluminium_other,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.alu.items.otherAluminiumAerosols'),
                  name: SampleCategories.aluminium_other_aerosols,
                },
              ],
            },
            {
              group: t('evidence.sampleFormFields.breakdowns.alu.groups.nonAluminium'),
              items: [
                {
                  label: t('evidence.sampleFormFields.breakdowns.alu.items.otherMetal'),
                  name: SampleCategories.metal_other,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.alu.items.nonMetal'),
                  name: SampleCategories.non_metal,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.alu.items.rigidPlastics'),
                  name: SampleCategories.aluminium_rigid_plastics,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.alu.items.vapes'),
                  name: SampleCategories.aluminium_vapes,
                },
              ],
            },
          ]}
          data-testid="alu-breakdown"
        />
      );

    case 'pet':
      return (
        <ManualBreakdownFormFields
          fields={[
            {
              group: t('evidence.sampleFormFields.breakdowns.pet.groups.petBottles'),
              items: [
                { label: t('evidence.sampleFormFields.breakdowns.pet.items.clear'), name: SampleCategories.pet_clear },
                {
                  label: t('evidence.sampleFormFields.breakdowns.pet.items.clearLarge'),
                  name: SampleCategories.pet_clear_large,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.pet.items.clearFullSleeve'),
                  name: SampleCategories.pet_clear_full_sleeve,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.pet.items.lightBlue'),
                  name: SampleCategories.pet_light_blue,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.pet.items.darkBlue'),
                  name: SampleCategories.pet_dark_blue,
                },
                { label: t('evidence.sampleFormFields.breakdowns.pet.items.green'), name: SampleCategories.pet_green },
                { label: t('evidence.sampleFormFields.breakdowns.pet.items.other'), name: SampleCategories.pet_other },
                {
                  label: t('evidence.sampleFormFields.breakdowns.pet.items.nonFoodGradeBottle'),
                  name: SampleCategories.non_food_grade,
                },
              ],
            },
            {
              group: t('evidence.sampleFormFields.breakdowns.pet.groups.otherPetAndPlastics'),
              items: [
                {
                  label: t('evidence.sampleFormFields.breakdowns.pet.items.clearPetTray'),
                  name: SampleCategories.clear_tray,
                },
                { label: t('evidence.sampleFormFields.breakdowns.pet.items.film'), name: SampleCategories.film },
                {
                  label: t('evidence.sampleFormFields.breakdowns.pet.items.otherPlastic'),
                  name: SampleCategories.plastic_other,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.pet.items.nonPlastic'),
                  name: SampleCategories.non_plastic,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.pet.items.prohibitive'),
                  name: SampleCategories.prohibitive,
                },
                { label: t('evidence.sampleFormFields.breakdowns.pet.items.liquid'), name: SampleCategories.liquid },
              ],
            },
          ]}
          data-testid="pet-breakdown"
        />
      );
    case 'zorba':
      return (
        <ManualBreakdownFormFields
          fields={[
            {
              group: t('evidence.sampleFormFields.breakdowns.zorba.groups.targetMaterial'),
              items: [
                {
                  label: t('evidence.sampleFormFields.breakdowns.zorba.items.nonFerrousMetal'),
                  name: SampleCategories.non_ferrous_metal,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.zorba.items.copperAndBrass'),
                  name: SampleCategories.copper_and_brass,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.zorba.items.postConsumerAluminiumPackaging'),
                  name: SampleCategories.aluminium_packaging,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.zorba.items.compositeMaterials'),
                  name: SampleCategories.composite,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.zorba.items.aluminiumFoilUbc'),
                  name: SampleCategories.aluminium_foil_ubc,
                },
              ],
            },
            {
              group: t('evidence.sampleFormFields.breakdowns.zorba.groups.nonTarget'),
              items: [
                {
                  label: t('evidence.sampleFormFields.breakdowns.zorba.items.otherMetalFerrous'),
                  name: SampleCategories.ferrous_metal,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.zorba.items.otherNonMetal'),
                  name: SampleCategories.non_metal,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.zorba.items.finesLt20mm'),
                  name: SampleCategories.fines,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.zorba.items.prohibitive'),
                  name: SampleCategories.prohibitive,
                },
              ],
            },
          ]}
          data-testid="zorba-breakdown"
        />
      );
    case 'taintTabor':
      return (
        <ManualBreakdownFormFields
          fields={[
            {
              group: t('evidence.sampleFormFields.breakdowns.taintTabor.groups.aluminium'),
              items: [
                {
                  label: t('evidence.sampleFormFields.breakdowns.taintTabor.items.taintTabor'),
                  name: SampleCategories.aluminium_tt,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.taintTabor.items.cast'),
                  name: SampleCategories.aluminium_cast,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.taintTabor.items.radiatorCondenser'),
                  name: SampleCategories.aluminium_radiator,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.taintTabor.items.ubcFoil'),
                  name: SampleCategories.aluminium_foil_ubc,
                },
              ],
            },
            {
              group: t('evidence.sampleFormFields.breakdowns.taintTabor.groups.aluWithAttachments'),
              items: [
                {
                  label: t('evidence.sampleFormFields.breakdowns.taintTabor.items.aluNonMetal'),
                  name: SampleCategories.aluminium_with_non_metal_attachments,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.taintTabor.items.aluIron'),
                  name: SampleCategories.aluminium_with_iron_attachments,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.taintTabor.items.castIron'),
                  name: SampleCategories.aluminium_cast_with_iron_attachments,
                },
              ],
            },
            {
              group: t('evidence.sampleFormFields.breakdowns.taintTabor.groups.nonAluminium'),
              items: [
                {
                  label: t('evidence.sampleFormFields.breakdowns.taintTabor.items.ferrousMetal'),
                  name: SampleCategories.ferrous_metal,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.taintTabor.items.stainlessSteel'),
                  name: SampleCategories.stainless_steel,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.taintTabor.items.otherMetal'),
                  name: SampleCategories.metal_other,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.taintTabor.items.nonMetal'),
                  name: SampleCategories.non_metal,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.taintTabor.items.finesLt20mm'),
                  name: SampleCategories.fines,
                },
                {
                  label: t('evidence.sampleFormFields.breakdowns.taintTabor.items.prohibitive'),
                  name: SampleCategories.prohibitive,
                },
              ],
            },
          ]}
          data-testid="taint-tabor-breakdown"
        />
      );
    default:
      return <DefaultBreakdownFormFields />;
  }
};

export const SampleFormFields = ({
  variant,
  disableBreakdown = false,
}: {
  variant?: SampleVariant;
  disableBreakdown?: boolean;
}) => {
  const { t } = useTranslation('features');

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <TextField
        name="total_weight"
        label={t('evidence.sampleFormFields.labels.totalWeight')}
        fullWidth
        required
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">{t('evidence.sampleFormFields.labels.weightUnit')}</InputAdornment>
            ),
          },
        }}
      />
      <TextField
        name="description"
        label={t('evidence.sampleFormFields.labels.notes')}
        variant="filled"
        rows={2}
        fullWidth
        multiline
      />
      {!disableBreakdown && <BreakdownFormFields variant={variant} />}
    </Stack>
  );
};
